import { Directive, ElementRef, HostListener, OnInit } from '@angular/core';

/**
 * Makes "buttons wearing an <a>" reachable by keyboard.
 *
 * The app has 84 anchors that carry a (click) handler and no href.
 * A browser only puts an anchor in the tab order if it has an href, so
 * none of them could be focused or activated without a mouse -- "Edit",
 * "Learn more about Your result", most modal triggers.
 *
 * These should really be <button> elements. Converting 84 of them across
 * 49 templates would change layout in places we cannot check visually
 * (<a> is inline, <button> is inline-block with its own metrics), so this
 * applies the standard ARIA fallback instead: expose them as buttons and
 * wire the keys a button would answer to.
 *
 * The selector deliberately excludes [routerLink]. Angular matches
 * directive selectors against the *static* template attributes, and
 * `<a routerLink="/x">` has no href at that point -- RouterLink adds it at
 * runtime. Without the exclusion this would tag 76 genuine navigation
 * links as buttons. (The V-035 CSS rule uses the same `:not([href])` idea
 * but is evaluated at runtime, where those anchors do have an href, so it
 * does not need the extra guard.)
 *
 * Existing role/tabindex on an element are left alone.
 */
@Directive({
  selector: 'a:not([href]):not([routerLink])',
  standalone: true,
})
export class NexleaderActionAnchorDirective implements OnInit {
  constructor(private el: ElementRef<HTMLAnchorElement>) {}

  ngOnInit(): void {
    const node = this.el.nativeElement;
    if (!node.hasAttribute('role')) node.setAttribute('role', 'button');
    if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '0');
  }

  /**
   * A native button fires click on both Enter and Space. Anchors do
   * neither without an href, so both are mapped here. Space is prevented
   * so the page doesn't scroll underneath the activation.
   */
  @HostListener('keydown.enter', ['$event'])
  @HostListener('keydown.space', ['$event'])
  onActivate(event: KeyboardEvent): void {
    event.preventDefault();
    this.el.nativeElement.click();
  }
}
